import MaterialReactTable from 'material-react-table'
import EditSharpIcon from '@mui/icons-material/EditSharp'
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import ArticleTwoToneIcon from '@mui/icons-material/ArticleTwoTone';
import PrintIcon from '@mui/icons-material/Print';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useMemo, useRef } from "react";
import {Button, Box, CircularProgress, Typography} from '@mui/material';
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import ReportExportDropdown from './ReportExportDropdown';

const ACTION_BUTTON_WIDTH = 24;

const countRowActionButtons = ({
    rowData,
    hideDelete,
    hidePrint,
    hasBrPrintHandler,
    hasReactivateHandler,
}) => {
    let count = 0;

    if (rowData?.hide_row_edit === undefined || rowData?.hide_row_edit === "No") {
        count += 1;
    }
    if (!hideDelete && (rowData?.hide_row_delete === undefined || rowData?.hide_row_delete === "No")) {
        count += 1;
    }
    if (
        hasBrPrintHandler &&
        rowData?.is_batch_report_needed === "Yes" &&
        rowData?.hide_row_print === "No"
    ) {
        count += 1;
    }
    if (!hidePrint && (rowData?.hide_row_print === undefined || rowData?.hide_row_print === "No")) {
        count += 1;
    }
    if (hasReactivateHandler && rowData?.show_row_reactivate === "Yes") {
        count += 1;
    }

    return count;
};

const getActionColumnWidth = ({ data, hideDelete, hidePrint, hasBrPrintHandler, hasReactivateHandler }) => {
    const minButtons = countRowActionButtons({
        rowData: {},
        hideDelete,
        hidePrint,
        hasBrPrintHandler,
        hasReactivateHandler,
    });

    // Sample only the first rows — scanning tens of thousands blocks the UI.
    const sample = (data || []).slice(0, 50);
    const maxRowButtons = sample.reduce((max, row) => {
        const rowCount = countRowActionButtons({
            rowData: row,
            hideDelete,
            hidePrint,
            hasBrPrintHandler,
            hasReactivateHandler,
        });
        return Math.max(max, rowCount);
    }, minButtons);

    const buttonCount = Math.max(maxRowButtons, 1);
    return buttonCount * ACTION_BUTTON_WIDTH + 12;
};

const headerMinSize = (headerName, maxWidth = 220) => {
    const text = String(headerName || '');
    if (!text || text === 'ROW_ACTIONS') {
        return 50;
    }
    return Math.min(maxWidth, Math.max(80, Math.ceil(text.length * 8.4 + 44)));
};

const Status = ({value,row}) => {
    const navigate = useNavigate(); 
    const handleClick = () => {
        // console.log('Button clicked!');
        Swal.fire({title: 'You cannot edit delivery challans related to this work schedule further..Do you wish to confirm?',  
        showCancelButton: true,  
        confirmButtonText: `Yes`,  
        cancelButtonText: `No`,
        }).then((result) => {  
        if (result.isConfirmed) { 
            navigate(`/JobCompletion/${row.original.id}/`);
        } 
        });
    };

    const handleCompletedClick = () => {
        navigate(`/JobCompletionReport/${row.original.id}/`);
    };

    const safeValue = value == null ? '' : String(value);
    const normalized = safeValue.trim().toLowerCase();
    if (normalized === 'active' || normalized === 'cancelled') {
        const isCancelled = normalized === 'cancelled';
        return (
            <Box
                component="span"
                sx={{
                    display: 'inline-block',
                    px: 1,
                    py: 0.25,
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: isCancelled ? '#7f1d1d' : '#065f46',
                    backgroundColor: isCancelled ? 'rgba(248, 113, 113, 0.22)' : 'rgba(16, 185, 129, 0.18)',
                    whiteSpace: 'nowrap',
                }}
            >
                {isCancelled ? 'Cancelled' : 'Active'}
            </Box>
        );
    }
    const colorMap = {
        not_started: 'warning',
        in_progress: 'primary',
        to_complete: 'success',
        completed: 'error'
    };
    const buttonColor = colorMap[safeValue] ; 
    
    // Replace hyphen with an empty string if underscore is present
    const transformedValue = safeValue.includes('_') ? safeValue.replace('_', ' ') : safeValue;

return (
    safeValue === 'in_progress' || safeValue === 'not_started' || safeValue === 'completed' || safeValue ==='to_complete'?
        (<Button 
            color={buttonColor} variant="contained" size="small"  
            onClick={
                safeValue === 'to_complete' ? handleClick
                : safeValue === 'completed' ? handleCompletedClick
                : undefined
            } 
        >{transformedValue}
        </Button>)
        :
        (<Box component="span"
            sx={{
                backgroundColor:
                    safeValue.toLowerCase() === 'active'? 'success.dark'
                        : safeValue.toLowerCase() === 'inactive' ? 'error.dark': 'warning.dark',
                borderRadius: '0.25rem',
                color: '#fff',
                maxWidth: '9ch',
                px: '0.3rem',
                py: '0.05rem',
                fontSize: '0.75rem',
                lineHeight: 1.1,
                textTransform: 'capitalize',
                fontWeight: 'bold'
            }}
        >{safeValue || '-'}
        </Box>)
    );
}

function ReactTableWrapper({
    title,
    columns,
    data,
    onRowEdit,
    onRowDelete,
    onCSVExport,
    onExcelExport,
    onPDFExport,
    enableExportToolbar = true,
    isLoading = false,
    hideDelete = false,
    hidePrint = true,
    onRowBrPrint,
    onRowPrint,
    onRowReactivate,
    left_pinned,right_pinned,
    compactColumns = false,
    compactRows = false,
    wrapText = false,
    enableTableSearch = true,
    initialSorting,
}) {
    compactColumns = true;
    wrapText = wrapText || true;

    const handlersRef = useRef({});
    handlersRef.current = { onRowEdit, onRowDelete, onRowBrPrint, onRowPrint, onRowReactivate };
    const hasBrPrintHandler = Boolean(onRowBrPrint);
    const hasReactivateHandler = Boolean(onRowReactivate);

    const denseCellSx = {
        padding: '4px 6px',
        fontSize: '12px',
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
        overflow: 'visible',
        textOverflow: 'clip',
        verticalAlign: 'middle',
    };

    const denseHeadSx = {
        padding: compactColumns ? '4px 6px' : '6px 6px',
        fontSize: compactColumns ? '12px' : '13px',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        height: 'auto',
        verticalAlign: 'bottom',
    };

    const tableHeadCellSx = {
        fontWeight: 700,
        letterSpacing: '0.02em',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 52%, #4f78db 100%)',
        backgroundColor: '#1e3a8a',
        color: '#ffffff',
        borderBottom: '2px solid #1e3a8a',
        borderRight: '1px solid rgba(255, 255, 255, 0.18)',
        ...denseHeadSx,
        cursor: 'pointer',
        overflow: 'visible !important',
        textOverflow: 'clip !important',
        '& .Mui-TableHeadCell-Content': {
            overflow: 'visible !important',
            width: '100%',
        },
        '& .Mui-TableHeadCell-Content-Labels, & .Mui-TableHeadCell-Content-Wrapper': {
            overflow: 'visible !important',
            whiteSpace: 'nowrap !important',
            textOverflow: 'clip !important',
        },
        '& .MuiTableSortLabel-root': {
            color: '#ffffff !important',
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            whiteSpace: 'nowrap !important',
            overflow: 'visible !important',
            textOverflow: 'clip !important',
            height: 'auto',
            lineHeight: 1.2,
            textAlign: 'left',
        },
        '& .MuiTableSortLabel-root .MuiTableSortLabel-icon': {
            alignSelf: 'center',
            marginTop: '0',
        },
        '& .MuiTableSortLabel-root.Mui-active': {
            color: '#ffffff !important',
        },
        '& .MuiTableSortLabel-icon': {
            color: '#ffffff !important',
            opacity: '0.7 !important',
            flexShrink: 0,
            marginLeft: '4px',
            fontSize: '18px',
        },
        '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon': {
            opacity: '1 !important',
            color: '#ffffff !important',
        },
        '& .MuiIconButton-root': {
            color: '#ffffff',
        },
    };

    const actionIconSx = {
        cursor: 'pointer',
        color: 'white',
        fontSize: '18px',
        margin: '0 1px',
        borderRadius: '3px',
        padding: '0px',
    };

    const actionColumnWidth = useMemo(
        () => getActionColumnWidth({ data, hideDelete, hidePrint, hasBrPrintHandler, hasReactivateHandler }),
        [data, hideDelete, hidePrint, hasBrPrintHandler, hasReactivateHandler]
    );

    const tableData = Array.isArray(data) ? data : [];
    const showInitialLoading = Boolean(isLoading) && tableData.length === 0;

    const tableColumns = useMemo(() => columns.map((col) => {

            let cellAlign = col.align || 'left';
            const headerWidth = headerMinSize(col.headerName, wrapText || compactColumns ? 520 : 220);
            let columnProperties = {
                accessorKey: col.fieldName,
                header: col.headerName,
                sortingFn: col.sortingFn || 'alphanumeric',
                ...(col.enableSorting !== undefined ? { enableSorting: col.enableSorting } : {}),
                ...(col.size !== undefined ? { size: Math.max(col.size, compactColumns ? headerWidth : col.size) } : compactColumns ? { size: headerWidth } : {}),
                ...(col.minSize !== undefined ? { minSize: col.minSize } : compactColumns ? { minSize: headerWidth } : {}),
                ...(col.maxSize !== undefined ? { maxSize: col.maxSize } : compactColumns || wrapText ? { maxSize: 720 } : {}),
            };
            

            if (col.headerName === 'ROW_ACTIONS') {
                columnProperties = {
                    ...columnProperties,
                    header: 'Action',
                    Header: <span style={col.headerStyle}>Action</span>,
                    size: actionColumnWidth,
                    minSize: actionColumnWidth,
                    maxSize: actionColumnWidth,
                    enableResizing: false,
                    enableSorting: false,
                    enableColumnFilter: false,
                    muiTableBodyCellProps: {
                        sx: {
                            ...denseCellSx,
                            overflow: 'visible',
                            width: `${actionColumnWidth}px`,
                            minWidth: `${actionColumnWidth}px`,
                            maxWidth: `${actionColumnWidth}px`,
                        },
                    },
                    muiTableHeadCellProps: {
                        sx: {
                            ...tableHeadCellSx,
                            cursor: 'default',
                            width: `${actionColumnWidth}px`,
                            maxWidth: `${actionColumnWidth}px`,
                        },
                    },
                    Cell: ({ row }) => {
                        const currentRowData = row.original;
                        const rowId = currentRowData.id;
                        const is_br_needed = currentRowData?.is_batch_report_needed;
                        const hide_row_edit = currentRowData?.hide_row_edit;
                        const hide_row_delete = currentRowData?.hide_row_delete;
                        const hide_row_print = currentRowData?.hide_row_print;
                        const show_row_reactivate = currentRowData?.show_row_reactivate;

                        return (
                            <span style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', lineHeight: 1 }}>
                                {(hide_row_edit == undefined ||  hide_row_edit === "No") && (
                                <EditSharpIcon onClick={(event) => handlersRef.current.onRowEdit(rowId, event)} 
                                sx={{
                                    ...actionIconSx,
                                    backgroundColor: 'primary.dark',
                                }}
                                />
                                )}
                                {((!hideDelete) && (hide_row_delete == undefined ||  hide_row_delete === "No")?
                                <DeleteForeverRoundedIcon onClick={(event) => handlersRef.current.onRowDelete(rowId, event)} 
                                sx={{
                                    ...actionIconSx,
                                    backgroundColor: 'error.dark',
                                }}
                                />
                                : null)}
                                
                                {((is_br_needed !== undefined) && (is_br_needed == 'Yes') && (hide_row_print === "No")) &&
                                    <ArticleTwoToneIcon  onClick={(event) => handlersRef.current.onRowBrPrint(rowId, event)}sx={{
                                        ...actionIconSx,
                                        backgroundColor: 'warning.dark',
                                    }}/>
                                }
                                 {((!hidePrint) && (hide_row_print == undefined ||  hide_row_print === "No")?
                                <PrintIcon
                                    titleAccess="Print Quotation"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handlersRef.current.onRowPrint?.(rowId, event);
                                    }}
                                    sx={{
                                    ...actionIconSx,
                                    backgroundColor: 'success.dark',
                                }}/>:null)}
                                {(hasReactivateHandler && show_row_reactivate === "Yes") ? (
                                <RestartAltIcon
                                    titleAccess="Reactivate Delivery Challan"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handlersRef.current.onRowReactivate?.(rowId, event);
                                    }}
                                    sx={{
                                        ...actionIconSx,
                                        backgroundColor: '#b45309',
                                    }}
                                />
                                ) : null}
                            </span>
                        );
                    }
                }
                
            } else if (col.headerName.toLowerCase() === 'status') {

                columnProperties = {
                    ...columnProperties,
                    header: col.headerName,
                    Cell: ({renderedCellValue,row}) => {

                        return (
                            <div style={{textAlign: cellAlign}}>
                                <Status value={renderedCellValue} row={row}/>
                            </div>
                        );
                    },
                    muiTableBodyCellProps: {
                        sx: denseCellSx,
                    },
                    muiTableHeadCellProps: {
                        sx: tableHeadCellSx,
                    },
                };

            } else {

                if (col.headerStyle) {
                    columnProperties = {
                        ...columnProperties,
                        Header: <span style={col.headerStyle}>{col.headerName}</span>
                    };
                }

                columnProperties = {
                    ...columnProperties,
                    Cell: ({renderedCellValue, row}) => {
                        const dynamicStyle = col.getCellStyle ? col.getCellStyle(row.original) : undefined;
                        const combinedStyle = (col.dataStyle || dynamicStyle)
                            ? { ...col.dataStyle, ...dynamicStyle }
                            : undefined;

                        return (
                            <div style={{
                                textAlign: cellAlign,
                                whiteSpace: 'nowrap',
                                lineHeight: wrapText ? 1.25 : 1.1,
                                overflow: compactColumns || wrapText ? 'visible' : 'hidden',
                                textOverflow: compactColumns || wrapText ? 'clip' : 'ellipsis',
                            }}>
                                {col.onCellClick ? (
                                    <button
                                        type="button"
                                        onClick={() => col.onCellClick(row.original)}
                                        style={{
                                            ...(combinedStyle || {}),
                                            background: 'none',
                                            border: 'none',
                                            padding: 0,
                                            cursor: 'pointer',
                                            font: 'inherit',
                                        }}
                                    >
                                        {renderedCellValue}
                                    </button>
                                ) : (
                                    combinedStyle ? <span style={combinedStyle}>{renderedCellValue}</span> : renderedCellValue
                                )}
                            </div>
                        
                        );
                    },
                    muiTableBodyCellProps: {
                        sx: denseCellSx,
                    },
                    muiTableHeadCellProps: {
                        sx: tableHeadCellSx,
                    },
                };
            }

            return columnProperties;
        }), [columns, hideDelete, hidePrint, actionColumnWidth, compactColumns, wrapText, hasReactivateHandler]);

    if (showInitialLoading) {
        return (
            <div style={{paddingTop: "24px", paddingBottom: "24px", textAlign: 'center'}}>
                <CircularProgress size={28} />
                <Typography sx={{ mt: 1, color: '#475569', fontSize: '0.9rem' }}>
                    Loading list…
                </Typography>
            </div>
        );
    }

    return (
        <div style={{paddingTop: "0px", paddingBottom: "4px"}}>
            <h1 style={{fontSize: '22px', color: 'darkblue', fontWeight: 'bold', display:'none'}}>{title}</h1>
            <MaterialReactTable
                columns={tableColumns}
                data={tableData}
                getRowId={(row) => String(row?.id ?? row?.__key ?? '')}
                enablePinning
                enableStickyHeader
                enableColumnResizing
                columnResizeMode="onEnd"
                enableColumnActions={false}
                // Bharat-style toolbar: search, filters, column show/hide, fullscreen
                enableColumnFilters={true}
                enableFilters={true}
                enableGlobalFilter={enableTableSearch}
                enableHiding={true}
                enableFullScreenToggle={true}
                enablePagination={true}
                enableSorting={true}
                enableBottomToolbar={true}
                enableTopToolbar={true}
                muiTableBodyRowProps={({ row }) => {
                    const original = row?.original || {};
                    const statusCandidates = [
                        original.status,
                        original.status_2,
                        original.customer_status,
                        original.cus_dc_status,
                        original.customer_dc_status,
                        original.dc_invioce,
                    ];
                    const isCancelledRow = statusCandidates.some((value) => {
                        const normalized = String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
                        return normalized === 'cancelled' || normalized === 'inactive';
                    });
                    return {
                        hover: true,
                        className: isCancelledRow ? 'mrt-row-cancelled' : undefined,
                        sx: {
                            height: wrapText ? 'auto' : '28px',
                            ...(isCancelledRow ? {
                                backgroundColor: '#fecaca !important',
                                '& td': {
                                    backgroundColor: '#fecaca !important',
                                    color: '#7f1d1d !important',
                                },
                            } : {}),
                        },
                    };
                }}
                enableDensityToggle={true}
                defaultColumn={compactColumns ? {
                    size: wrapText ? 160 : 120,
                    minSize: wrapText ? 120 : 80,
                    maxSize: 720,
                } : {
                    size: 140,
                    minSize: 90,
                    maxSize: 480,
                }}
                muiTablePaperProps={{
                    elevation: 0,
                    sx: {
                      borderRadius: '0',
                      border: '1px solid #000000',
                      overflow: 'hidden',
                      width: '100%',
                      maxWidth: '100%',
                    },
                  }}
                positionToolbarDropZone="none"
                muiTopToolbarProps={{
                    sx: {
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'nowrap',
                      minHeight: '42px !important',
                      height: 'auto',
                      padding: '0 !important',
                      gap: 0,
                      overflow: 'hidden',
                      '& > .MuiBox-root': {
                        padding: '4px 8px !important',
                        alignItems: 'center',
                        minHeight: '0 !important',
                      },
                      '& > .MuiFade-root, & .Mui-ToolbarDropZone': {
                        display: 'none !important',
                      },
                      '& .MuiCollapse-root': {
                        minHeight: '0 !important',
                      },
                      '& .MuiLinearProgress-root': {
                        display: 'none',
                      },
                      '@media (min-width: 600px)': {
                        minHeight: '42px !important',
                      },
                    },
                  }}
                muiTableContainerProps={{
                    sx: {
                      overflowX: 'auto',
                      overflowY: 'auto',
                      maxWidth: '100%',
                      width: '100%',
                    },
                  }}
                muiTableProps={{
                    sx: {
                      width: 'max-content',
                      minWidth: '100%',
                      tableLayout: 'auto',
                    },
                  }}
                  muiTableBodyProps={{
                    sx: () => ({
                      '& tr:nth-of-type(odd):not(.mrt-row-cancelled)': {
                        backgroundColor:'#bcbfc4',
                      },
                      '& tr.mrt-row-cancelled, & tr.mrt-row-cancelled > td': {
                        backgroundColor: '#fecaca !important',
                        color: '#7f1d1d !important',
                      },
                      '& td': {
                        paddingTop: '0px',
                        paddingBottom: '0px',
                        paddingLeft: '6px',
                        paddingRight: '6px',
                        fontSize: compactColumns ? '12px' : '13px',
                        lineHeight: 1.1,
                        height: wrapText ? 'auto' : '28px',
                        overflow: 'visible !important',
                        textOverflow: 'clip !important',
                        whiteSpace: 'nowrap !important',
                        verticalAlign: 'middle',
                      },
                      '& td:last-of-type': {
                        overflow: 'visible',
                        whiteSpace: 'nowrap',
                      },
                    }),
                  }}
                muiTableHeadProps={{
                    sx: {
                      '& .MuiTableCell-head': {
                        whiteSpace: 'nowrap !important',
                        height: 'auto',
                        verticalAlign: 'bottom',
                        overflow: 'visible !important',
                        textOverflow: 'clip !important',
                      },
                      '& .Mui-TableHeadCell-Content, & .Mui-TableHeadCell-Content-Labels, & .Mui-TableHeadCell-Content-Wrapper': {
                        whiteSpace: 'nowrap !important',
                        overflow: 'visible !important',
                        textOverflow: 'clip !important',
                      },
                    },
                  }}
                muiTableHeadCellProps={{
                    sx: tableHeadCellSx,
                  }}
                positionToolbarAlertBanner="none"
                initialState={
                    {
                        density: "compact",
                        showGlobalFilter: false,
                        showColumnFilters: false,
                        pagination: {pageIndex: 0, pageSize: 5},
                        columnPinning: { left: left_pinned, right: right_pinned || ['id'] },
                        ...(Array.isArray(initialSorting) && initialSorting.length
                            ? { sorting: initialSorting }
                            : {}),
                    }
                }
                muiPaginationProps={{
                    rowsPerPageOptions: [5, 10, 25, 50, 100],
                    showFirstButton: true,
                    showLastButton: true,
                }}
                renderTopToolbarCustomActions={
                    () => {
                        if (enableExportToolbar) {
                            return (
                                <ReportExportDropdown
                                    onExcel={onExcelExport || onCSVExport}
                                    onCSV={onExcelExport ? onCSVExport : undefined}
                                    onPDF={onPDFExport}
                                    disabled={!tableData?.length}
                                />
                            );
                        }
                        return null;
                    }
                }
            />
        </div>
    );
}

export default ReactTableWrapper;